/**
 * Trial-end nudge banner for invite-trial subscribers.
 *
 * Shows in the t-3 window (when an invite-trial SUBSCRIBER's
 * subscriptionEndsAt is within 3 days). One click goes into Stripe
 * Checkout for the monthly plan; yearly is still reachable via the
 * header's Upgrade button.
 *
 * Dismiss hides for the current calendar day (aiprompt:trialBannerDismissedOn
 * keyed by YYYY-MM-DD). The banner reappears tomorrow if the user is
 * still in the window — keeps the nudge gentle but persistent.
 *
 * Mobile: stacks vertically below 520px so the CTA + dismiss don't
 * crowd each other on narrow viewports.
 */
import { useEffect, useState } from 'react';
import type { AuthUser } from '../hooks/useAuth';

interface TrialBannerProps {
  user: AuthUser;
}

const DISMISS_KEY = 'aiprompt:trialBannerDismissedOn:v1';
const DAYS_BEFORE_END_TO_SHOW = 3;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  return Math.ceil((end - Date.now()) / (24 * 60 * 60 * 1000));
}

function formatTrialEndDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function TrialBanner({ user }: TrialBannerProps) {
  const [dismissedToday, setDismissedToday] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(DISMISS_KEY) === todayStamp();
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Covers all non-Stripe SUBSCRIBERs whose access has a finite end:
  // invite-trial ('trialing'), admin manual gift ('admin_gift'), and
  // partner-domain auto-grant ('partner_grant'). All three benefit from
  // the same t-3 nudge to add a payment method before the access lapses.
  const isNonStripeSubscriber =
    user.role === 'SUBSCRIBER' &&
    !user.hasStripeCustomer &&
    ['trialing', 'admin_gift', 'partner_grant'].includes(user.subscriptionStatus || '');
  const days = daysUntil(user.subscriptionEndsAt);
  const inWindow =
    isNonStripeSubscriber && days != null && days <= DAYS_BEFORE_END_TO_SHOW && days >= 0;

  // Reset dismissal if the date has rolled over while the tab stayed open.
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(DISMISS_KEY) !== todayStamp()) {
      setDismissedToday(false);
    }
  }, [days]);

  if (!inWindow || dismissedToday) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, todayStamp());
    } catch {
      /* ignore */
    }
    setDismissedToday(true);
  };

  const addPayment = async () => {
    setError(null);
    setBusy(true);
    try {
      const csrf = readCookie('aiprompt_csrf');
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({ interval: 'month' }),
      });
      const json = (await res.json().catch(() => null)) as
        | { url?: string; error?: { message?: string } }
        | null;
      if (!res.ok || !json?.url) {
        throw new Error(json?.error?.message || `HTTP ${res.status}`);
      }
      window.location.href = json.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const dateLabel = user.subscriptionEndsAt
    ? formatTrialEndDate(user.subscriptionEndsAt)
    : null;
  // Use "Trial" wording only for invite-trial users; admin gifts and
  // partner grants read as plain "Subscription ends..." so the copy
  // matches what the user knows about their access.
  const verb = user.subscriptionStatus === 'trialing' ? 'Trial' : 'Subscription';
  const dayPhrase =
    days === 0
      ? `${verb} ends today`
      : days === 1
        ? `${verb} ends tomorrow`
        : `${verb} ends in ${days} days${dateLabel ? ` (${dateLabel})` : ''}`;

  return (
    <div className="trial-banner" role="status">
      <div className="trial-banner__body">
        <span className="trial-banner__icon" aria-hidden="true">⏰</span>
        <div className="trial-banner__text">
          <strong>{dayPhrase}</strong>
          <span className="trial-banner__hint">
            Add a payment method to keep unlimited access. Cancel anytime.
          </span>
        </div>
      </div>
      <div className="trial-banner__actions">
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => void addPayment()}
          disabled={busy}
        >
          {busy ? 'Opening…' : 'Add payment'}
        </button>
        <button
          type="button"
          className="trial-banner__dismiss"
          onClick={dismiss}
          aria-label="Dismiss for today"
          title="Dismiss for today"
        >
          ×
        </button>
      </div>
      {error && <div className="trial-banner__error">{error}</div>}
    </div>
  );
}
